import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowDownRight, Copy, RefreshCw } from 'lucide-react';
import { Badge, ErrorState, LoadingState, describeError } from '@/components/primitives';
import useResource from '@/pages/dashboard/useResource';
import { API, apiGet, authHeaders, copyText } from '@/pages/dashboard/api';
import { ago } from '@/pages/dashboard/format';

/*
 * Email protection: can someone send mail that appears to come from your domain, such as a message from your
 * CEO asking finance for a payment? SPF, DKIM and DMARC decide. We read the public DNS records of each
 * protected domain, grade them, and say exactly what to change. DNS only; no mail server is contacted.
 */

const SEV = { high: { label: 'Fix now', tone: 'danger' }, medium: { label: 'Fix soon', tone: 'warning' }, low: { label: 'Improve', tone: 'neutral' }, info: { label: 'Note', tone: 'neutral' }, pass: { label: 'Good', tone: 'success' } };
const AREAS = [['spf', 'SPF'], ['dmarc', 'DMARC'], ['dkim', 'DKIM'], ['mta_sts', 'Transport']];
const STATE = { high: 'fail', medium: 'warn', low: 'ok', pass: 'ok', info: 'none' };
const RANK = ['high', 'medium', 'low', 'info', 'pass'];

function areaState(findings, area) {
  const mine = findings.filter((f) => f.area === area);
  if (!mine.length) return 'none';
  const worst = RANK.find((r) => mine.some((f) => f.severity === r));
  return STATE[worst];
}
const STATE_LABEL = { fail: 'Failing', warn: 'Weak', ok: 'In place', none: 'Not seen' };

function DomainCard({ r }) {
  const [open, setOpen] = useState(false);
  const letter = r.grade.replace('?', '');
  const dropped = r.previous_grade && r.previous_grade.replace('?', '') !== letter && 'ABCDF'.indexOf(letter) > 'ABCDF'.indexOf(r.previous_grade.replace('?', ''));
  const copy = async (text) => { if (await copyText(text)) toast.success('Copied the record'); };
  return (
    <section className="br-card br-auth" data-testid={`auth-${r.domain}`} aria-label={`Email protection for ${r.domain}`}>
      <div className="br-auth-h">
        <span className={`br-grade br-grade--${letter}`} aria-label={`Grade ${r.grade}`}>{r.grade}</span>
        <div className="br-auth-title">
          <h2 className="br-h2 br-mono-h">{r.domain}</h2>
          <p className="br-muted">{r.sends_mail ? 'Sends and receives mail' : 'Receives no mail'} · score {r.score} of 100 · checked {ago(r.checked_at)}</p>
        </div>
        {dropped && <Badge size="sm" tone="danger" icon={ArrowDownRight}>Was {r.previous_grade}</Badge>}
      </div>
      <div className="br-areas" role="list" aria-label="Protection by area">
        {AREAS.map(([id, label]) => {
          const st = areaState(r.findings, id);
          return <span key={id} role="listitem" className={`br-area br-area--${st}`}><b>{label}</b>{STATE_LABEL[st]}</span>;
        })}
      </div>
      {r.incomplete && <p className="br-warn" role="status">Some lookups failed, so this grade may change. Check again shortly.</p>}
      <ul className="br-findings">
        {r.findings.map((f) => (
          <li key={f.id} className={`br-finding br-finding--${f.severity}`}>
            <div className="br-finding-h"><Badge size="sm" tone={SEV[f.severity].tone}>{SEV[f.severity].label}</Badge><b>{f.title}</b></div>
            <p>{f.detail}</p>
            {f.fix && <p className="br-fix"><span className="br-muted">What to do.</span> {f.fix}</p>}
            {f.record && f.severity !== 'pass' && (
              <div className="br-record"><code>{f.record}</code>
                <button type="button" className="br-icon-btn" onClick={() => copy(f.record)} aria-label="Copy this record"><Copy aria-hidden="true" /></button></div>
            )}
          </li>
        ))}
      </ul>
      <button type="button" className="br-more" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'} the records we read</button>
      {open && (
        <dl className="br-facts br-facts--wide">
          <div><dt>SPF</dt><dd className="br-mono">{r.records.spf || 'None published'}</dd></div>
          <div><dt>DMARC</dt><dd className="br-mono">{r.records.dmarc || 'None published'}</dd></div>
          <div><dt>DKIM keys found</dt><dd className="br-mono">{r.records.dkim_selectors.length ? r.records.dkim_selectors.join(', ') : 'None under common names'}</dd></div>
          <div><dt>SPF lookups</dt><dd>{r.records.spf_lookups ?? '-'} of 10 allowed</dd></div>
          <div><dt>Mail servers</dt><dd className="br-mono">{r.records.mx.length ? r.records.mx.join(', ') : 'None'}</dd></div>
        </dl>
      )}
    </section>
  );
}

export default function EmailProtection() {
  const res = useResource(() => apiGet('/brand/email-auth'), [], { poll: false });
  const [busy, setBusy] = useState(false);
  const d = res.data;
  const check = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/brand/email-auth/check`, {}, { headers: authHeaders() });
      await res.reload();
      toast.success('Email protection checked');
    } catch (err) {
      toast.error("Couldn't check email protection", { description: describeError(err) });
    } finally {
      setBusy(false);
    }
  };
  const none = d && !d.results.length && !d.unchecked.length;
  return (
    <div className="br-tab" data-testid="brand-email">
      <div className="br-toolbar">
        <p className="br-fine">Can someone send mail that looks like it comes from you? We read each protected domain&apos;s public SPF, DKIM and DMARC records and say what to change. {d?.note}</p>
        <div className="br-toolbar-actions">
          <button type="button" className="is-btn is-btn--primary is-btn--sm" onClick={check} disabled={busy || none} data-testid="check-email">
            <RefreshCw className={busy ? 'is-spinner' : undefined} aria-hidden="true" /> {busy ? 'Checking…' : d?.results.length ? 'Check again' : 'Check now'}
          </button>
        </div>
      </div>
      {res.loading && !d ? <LoadingState label="email protection" rows={4} />
        : res.error && !d ? <ErrorState title="Couldn't load email protection" error={res.error} onRetry={res.reload} />
          : none ? <div className="br-empty"><b>Add a domain first</b><span>Email protection is checked for the domains you protect. Add one on the Lookalike domains tab.</span></div>
            : !d.results.length ? <div className="br-empty" data-testid="auth-empty"><b>Not checked yet</b><span>Check now to grade the email records of {d.unchecked.join(', ')}. It takes a few seconds and changes nothing.</span></div>
              : (
                <>
                  {d.results.map((r) => <DomainCard key={r.domain} r={r} />)}
                  {d.unchecked.length > 0 && <p className="br-fine">Not checked yet: {d.unchecked.join(', ')}.</p>}
                  <p className="br-fine">We re-check every day and alert you if a record is removed or weakened. DKIM selectors are arbitrary, so not finding one is never reported as a failure.</p>
                </>
              )}
    </div>
  );
}
